import type { OpportunityInfo } from '../../stores/klanten-store';
import { formatCurrency, formatDate } from '../../lib/format';
import clsx from 'clsx';

/**
 * Tab-inhoud "Doorverwijzingen" — Cinco-platform aanvragen.
 *
 * Cinco-platform aanvragen leven als Opportunity met Brand__c='CINCO' en worden
 * al meegestuurd door /api/customers/detail. Elke regel is klikbaar en opent —
 * net als een order — de detail-drawer (OpportunityDetailDrawerPanel). De tab
 * zelf verschijnt alleen als er minstens één Cinco-opportunity is (zie
 * KlantDetailView).
 */
interface CincoPlatformTabProps {
  referrals: OpportunityInfo[];
  onOpenReferral: (opportunityId: string) => void;
}

// Cinco-opp StageName → badge-kleur. Onbekende stages vallen terug op slate.
function stageBadgeClass(stage: string): string {
  switch (stage) {
    case 'Doorverwezen':
      return 'bg-indigo-100 text-indigo-700';
    case 'Gewonnen':
    case 'Closed Won':
      return 'bg-emerald-100 text-emerald-700';
    case 'Verloren':
    case 'Closed Lost':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-slate-100 text-slate-600';
  }
}

export function CincoPlatformTab({ referrals, onOpenReferral }: CincoPlatformTabProps) {
  if (referrals.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-32 text-slate-400">
        <p className="text-sm">Geen doorverwijzingen via het Cinco-platform</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-slate-100">
      {referrals.map((r) => {
        const dienst = r.segment === 'SR' ? 'Reiniging op locatie (SR)' : r.segment || r.offerType;
        return (
          <button
            key={r.id}
            type="button"
            onClick={() => onOpenReferral(r.id)}
            className="block w-full text-left px-6 py-4 hover:bg-slate-50 transition-colors cursor-pointer"
          >
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-2 min-w-0">
                {r.opportunityNumber ? (
                  <span className="text-sm font-medium text-slate-900">{r.opportunityNumber}</span>
                ) : null}
                <span className={clsx('text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0', stageBadgeClass(r.stage))}>
                  {r.stage || 'Cinco'}
                </span>
              </div>
              {r.totalInclBtw ? (
                <span className="text-sm font-medium text-slate-900 flex-shrink-0">{formatCurrency(r.totalInclBtw)}</span>
              ) : null}
            </div>

            {/* Naar wie doorverwezen */}
            {r.partnerName ? (
              <p className="text-sm text-slate-700">
                Doorverwezen naar <span className="font-medium">{r.partnerName}</span>
                {r.doorverwezenPlaats ? <span className="text-slate-500"> · {r.doorverwezenPlaats}</span> : null}
              </p>
            ) : null}

            {/* Meta: dienst, commissie, datum */}
            <div className="mt-1 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
              {dienst ? <span>{dienst}</span> : null}
              {r.commissionAmount ? <span>Commissie {formatCurrency(r.commissionAmount)}</span> : null}
              {r.createdDate ? <span>{formatDate(r.createdDate)}</span> : null}
            </div>
          </button>
        );
      })}
    </div>
  );
}
